'use client'

import { useCallback, useMemo, useState } from 'react'
import Link from 'next/link'
import { IconArrowRight, IconChevronDown, IconSearch } from '@tabler/icons-react'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import { branding } from '@/lib/branding.config'

export interface FaqCategory {
  name: string
  faqs: { question: string; answer: string }[]
}

interface FaqPageClientProps {
  categories: FaqCategory[]
}

/** Category chip: neutral by default, a primary tint (foreground label) when selected. */
const categoryClass = (active: boolean) =>
  cn(
    'whitespace-nowrap rounded-xl px-3 py-2 text-sm transition-colors border lg:w-full lg:text-left',
    active
      ? 'bg-primary/10 text-foreground border-primary/30 font-medium'
      : 'border-transparent text-muted-foreground hover:text-foreground hover:bg-muted'
  )

/**
 * The interactive part of /faq: search, category filter and the accordions.
 * The page itself is a server component so it can export metadata; the
 * questions are passed in from there.
 */
export function FaqPageClient({ categories }: FaqPageClientProps) {
  const [searchQuery, setSearchQuery] = useState('')
  const [openItems, setOpenItems] = useState<Record<string, boolean>>({})
  const [selectedCategoryName, setSelectedCategoryName] = useState<string | null>(null)

  const toggleItem = (key: string) => {
    setOpenItems(prev => ({ ...prev, [key]: !prev[key] }))
  }

  const categoryIdForName = useCallback((name: string) => {
    const slug = name
      .toLowerCase()
      .trim()
      .replace(/&/g, 'and')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '')
    return `faq-${slug || 'category'}`
  }, [])

  const matches = useCallback(
    (faq: FaqCategory['faqs'][number], query: string) =>
      faq.question.toLowerCase().includes(query.toLowerCase()) ||
      faq.answer.toLowerCase().includes(query.toLowerCase()),
    []
  )

  const filteredCategories = useMemo(
    () =>
      categories
        .map(category => ({ ...category, faqs: category.faqs.filter(faq => matches(faq, searchQuery)) }))
        .filter(category => category.faqs.length > 0),
    [categories, matches, searchQuery]
  )

  const displayedCategories = useMemo(() => {
    if (!selectedCategoryName) return filteredCategories
    const match = filteredCategories.find(c => c.name === selectedCategoryName)
    return match ? [match] : filteredCategories
  }, [filteredCategories, selectedCategoryName])

  const navCategories = useMemo(
    () =>
      filteredCategories.map(category => ({
        name: category.name,
        id: categoryIdForName(category.name),
      })),
    [filteredCategories, categoryIdForName]
  )

  const selectCategory = (name: string | null) => {
    setSelectedCategoryName(name)
    document.getElementById('faq-questions')?.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }

  return (
    <div className="min-h-screen bg-background text-foreground">
      {/* Hero Section */}
      <section className="relative overflow-hidden border-b border-border">
        <div aria-hidden className="pointer-events-none absolute inset-0">
          <div className="absolute inset-0 bg-grid-pattern opacity-40" />
          <div className="orb-primary -left-44 -top-44 h-[580px] w-[580px]" />
          <div className="orb-primary-soft -right-56 -top-28 h-[640px] w-[640px]" />
        </div>
        <div className="relative max-w-6xl mx-auto px-4 py-16 md:py-24">
          <div className="grid items-center gap-10 md:grid-cols-12">
            <div className="md:col-span-7">
              <div className="inline-flex items-center gap-2 rounded-full border border-border bg-card px-3 py-1 text-xs text-foreground">
                <span aria-hidden className="h-1.5 w-1.5 rounded-full bg-primary ring-4 ring-primary/15" />
                Help Center
              </div>
              <h1 className="mt-5 text-4xl md:text-5xl font-bold tracking-tight text-foreground">
                Frequently Asked Questions
              </h1>
              <p className="mt-4 text-lg md:text-xl text-muted-foreground max-w-2xl">
                Answers about {branding.name} GPU machines: pricing, billing, regions, connecting and more.
              </p>

              <div className="mt-6 flex flex-wrap items-center gap-3 text-sm">
                <Button asChild variant="outline" size="sm" className="rounded-xl">
                  <Link href="/help">
                    Contact support
                    <IconArrowRight className="size-4" />
                  </Link>
                </Button>
                <span className="text-muted-foreground">or browse the categories below</span>
              </div>
            </div>

            {/* Search card */}
            <div className="md:col-span-5">
              <div className="rounded-2xl border border-border bg-card/80 text-card-foreground p-5 md:p-6 backdrop-blur">
                <div className="flex items-center justify-between gap-4">
                  <label htmlFor="faq-search" className="text-sm font-medium text-foreground">
                    Search the FAQ
                  </label>
                  <p className="text-xs text-muted-foreground">Type to filter</p>
                </div>

                <div className="relative mt-3">
                  <IconSearch className="absolute left-4 top-1/2 -translate-y-1/2 size-5 text-muted-foreground" aria-hidden />
                  <input
                    id="faq-search"
                    type="text"
                    placeholder="Search for answers..."
                    value={searchQuery}
                    onChange={(e) => {
                      const nextQuery = e.target.value
                      setSearchQuery(nextQuery)

                      if (!selectedCategoryName) return
                      const hasMatchesInSelected = categories
                        .find(c => c.name === selectedCategoryName)
                        ?.faqs.some(faq => matches(faq, nextQuery))
                      if (!hasMatchesInSelected) setSelectedCategoryName(null)
                    }}
                    className="w-full h-12 pl-12 pr-4 rounded-xl bg-background border border-border outline-none focus:border-primary/60 focus:ring-2 focus:ring-primary/20 transition-all text-foreground placeholder:text-muted-foreground"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16">
        <div className="max-w-6xl mx-auto px-4">
          {filteredCategories.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-foreground mb-4">
                No results found for <span className="font-semibold">&quot;{searchQuery}&quot;</span>
              </p>
              <Button variant="outline" onClick={() => setSearchQuery('')}>
                Clear Search
              </Button>
            </div>
          ) : (
            <div className="grid gap-10 lg:grid-cols-[260px_1fr]">
              {/* Category menu */}
              {/* min-w-0: a grid item otherwise grows to its content's width, so on
                  phones the horizontally scrolling category row widened the page. */}
              <aside className="min-w-0 lg:sticky lg:top-24 lg:self-start">
                <div className="rounded-2xl border border-border bg-card text-card-foreground p-4">
                  <p className="text-xs font-medium tracking-wide uppercase text-muted-foreground mb-3">
                    Categories
                  </p>
                  <nav className="flex gap-2 overflow-x-auto pb-1 lg:flex-col lg:overflow-visible lg:pb-0">
                    <button
                      type="button"
                      onClick={() => selectCategory(null)}
                      className={categoryClass(selectedCategoryName === null)}
                    >
                      All
                    </button>
                    {navCategories.map(category => (
                      <button
                        type="button"
                        key={category.id}
                        className={categoryClass(selectedCategoryName === category.name)}
                        onClick={() => selectCategory(category.name)}
                      >
                        {category.name}
                      </button>
                    ))}
                  </nav>
                </div>
              </aside>

              {/* Categories + questions */}
              <div id="faq-questions" className="min-w-0 space-y-10 scroll-mt-24">
                {selectedCategoryName ? (
                  <div className="flex items-center justify-between gap-4">
                    <p className="text-sm text-muted-foreground">
                      Showing: <span className="text-foreground">{selectedCategoryName}</span>
                    </p>
                    <Button variant="outline" size="sm" onClick={() => selectCategory(null)}>
                      Clear filter
                    </Button>
                  </div>
                ) : null}

                {displayedCategories.map((category) => {
                  const categoryId = categoryIdForName(category.name)

                  return (
                    <div key={category.name} id={categoryId} className="scroll-mt-24">
                      <h2 className="text-xl font-bold text-foreground mb-4">{category.name}</h2>
                      <div className="space-y-3">
                        {category.faqs.map((faq, index) => {
                          const key = `${category.name}-${index}`
                          const isOpen = openItems[key]
                          const panelId = `${categoryId}-${index}`

                          return (
                            <div
                              key={key}
                              className={cn(
                                'rounded-xl overflow-hidden transition-all duration-200 bg-card text-card-foreground border',
                                isOpen
                                  ? 'border-primary/40 shadow-lg shadow-primary/5'
                                  : 'border-border hover:border-primary/30'
                              )}
                            >
                              <button
                                type="button"
                                onClick={() => toggleItem(key)}
                                aria-expanded={isOpen}
                                aria-controls={panelId}
                                className="w-full flex items-center justify-between p-5 text-left"
                              >
                                <span className="font-medium pr-4 text-foreground">{faq.question}</span>
                                <IconChevronDown
                                  className={cn(
                                    'size-5 shrink-0 transition-transform duration-200',
                                    isOpen ? 'rotate-180 text-primary' : 'text-muted-foreground'
                                  )}
                                  aria-hidden
                                />
                              </button>
                              <div
                                id={panelId}
                                className={cn(
                                  'overflow-hidden transition-all duration-200',
                                  isOpen ? 'max-h-[800px]' : 'max-h-0'
                                )}
                              >
                                <p className="px-5 pb-5 text-muted-foreground leading-relaxed">{faq.answer}</p>
                              </div>
                            </div>
                          )
                        })}
                      </div>
                    </div>
                  )
                })}
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Still Have Questions */}
      <section className="relative py-16 md:py-24 border-t border-border overflow-hidden">
        <div className="relative max-w-5xl mx-auto px-4">
          <div className="relative overflow-hidden rounded-3xl border border-border bg-card text-card-foreground px-6 py-12 md:px-10 md:py-16 text-center">
            <div aria-hidden className="absolute inset-0 bg-hero-mesh" />
            <div className="relative">
              <h2 className="text-3xl md:text-5xl font-bold text-foreground tracking-tight">
                Still Have Questions?
              </h2>
              <p className="mt-4 text-lg md:text-xl text-muted-foreground max-w-2xl mx-auto">
                Can&apos;t find what you&apos;re looking for? Our support team is here to help.
              </p>
              <Button asChild variant="primary" size="lg" className="mt-10 rounded-full px-10">
                <Link href="/help">
                  Contact Support
                  <IconArrowRight className="size-4" />
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </section>
    </div>
  )
}
